import { filterVisibleBanners } from '@superbet-group/web.lib.utilities';
import {
    createSimpleGetter,
    createSimpleMutator,
} from '@superbet-group/web.lib.vue-utilities';
import {
    Banner,
    BannerCategory,
    isBannerDirectusArray,
} from '@superbet-group/web.types';
import { Commit } from 'vuex';

import { QuickLink } from '@shared/banners/QuickLink';
import { RelativeRootGetters } from '@store/types';
import { asyncErrorGuard } from '@utils/ErrorGuard';
import { filterValidBanners } from './helpers';
import {
    bannersService,
    quickLinksService,
} from '@src/federations/shared/sharedServiceInstances';
import { QuickLinkLocation } from '@src/federations/shared/cms/quickLinks/QuickLinksService';

const SET_QUICK_LINKS_V2 = 'SET_QUICK_LINKS_V2';
const SET_PROMOTIONS_V2 = 'SET_PROMOTIONS_V2';
const SET_REGISTRATION = 'SET_REGISTRATION';

export interface BannersState {
    [BannerCategory.PromotionsV2]: Banner[];
    [BannerCategory.Registration]: Banner[];
    quickLinksV2: QuickLink[];
}

function defaultState(): BannersState {
    return {
        [BannerCategory.PromotionsV2]: [],
        [BannerCategory.Registration]: [],
        quickLinksV2: [],
    };
}

const store = {
    state: defaultState(),

    mutations: {
        [SET_QUICK_LINKS_V2]: createSimpleMutator<BannersState, 'quickLinksV2'>('quickLinksV2'),
        [SET_PROMOTIONS_V2]: createSimpleMutator<BannersState, 'promotionsV2'>(BannerCategory.PromotionsV2),
        [SET_REGISTRATION]: createSimpleMutator<BannersState, 'registration'>(BannerCategory.Registration),
    },

    getters: {
        promotionBannersV2: createBannersGetter(BannerCategory.PromotionsV2),
        quickLinksV2: createSimpleGetter<BannersState, 'quickLinksV2'>('quickLinksV2'),
        registration: createBannersGetter(BannerCategory.Registration),

    },

    actions: {
        promotionBannersV2: fetchBannerAction(BannerCategory.PromotionsV2, SET_PROMOTIONS_V2),
        registration: fetchBannerAction(BannerCategory.Registration, SET_REGISTRATION),
        fetchSportQuickLinksV2: asyncErrorGuard(async (
            { commit }: { commit: Commit },
        ) => {
            let quickLinks = await quickLinksService.getQuickLinks(QuickLinkLocation.Sport);
            commit(SET_QUICK_LINKS_V2, quickLinks);
        }),
    },

    namespaced: true,
};

export default store;
export type BannersStore = typeof store;

function fetchBannerAction(bannerCategory: BannerCategory, mutation: string) {
    return asyncErrorGuard <BannersState, unknown, void, Promise<Banner[]>>(async ({ commit }: {commit: Commit }) => {
        const banners = await bannersService.getBanners(bannerCategory);

        commit(mutation, banners);
        return banners;
    });
}

function createBannersGetter(category: keyof Omit<BannersState, 'areDirectusBannersEnabled' | 'quickLinksV2'>) {
    return function (state: BannersState, getters: unknown, rootState: unknown, rootGetters: RelativeRootGetters) {
        const banners = state[category];
        if (isBannerDirectusArray(banners)) {
            const isLoggedIn = !!rootGetters['data/authentication/user'];
            const isReturningUserDevice = !!rootGetters['data/authentication/isReturningUserDevice'];
            const now = new Date();
            const visibleBanners = filterVisibleBanners(banners, now, isLoggedIn, isReturningUserDevice);

            return filterValidBanners(visibleBanners, category);
        } else {
            return banners;
        }
    };
}
